import { createContext, useContext } from "react";
import { formatISO } from "date-fns";
import { InvoiceViewOptions, InvoiceStats } from "@common/types/invoice";
import { InvoiceFilters } from "@common/contracts/invoice.contract";
import { omit } from "@common/helpers/objects";

export type InvoiceViewFilters = InvoiceFilters & { invoiceIds?: string[] };

export function toInvoiceApiFilters(
    filters: InvoiceViewFilters | undefined,
): InvoiceFilters | undefined {
    if (!filters) {
        return undefined;
    }
    const { invoiceIds, ...rest } = omit(filters, [
        "start",
        "end",
        "startDate",
        "endDate",
    ]);
    const isAllTime =
        rest.createdOnFrom === undefined && rest.createdOnTo !== undefined;

    return {
        ...rest,
        ...(isAllTime
            ? { createdOnTo: formatISO(new Date(), { representation: "date" }) }
            : {}),
        ...(invoiceIds ? { ids: invoiceIds } : {}),
    };
}

interface InvoicesViewContextValue {
    filters: InvoiceViewFilters | undefined;
    setFilters: React.Dispatch<
        React.SetStateAction<InvoiceViewFilters | undefined>
    >;

    viewOption: InvoiceViewOptions;
    setViewOption: React.Dispatch<React.SetStateAction<InvoiceViewOptions>>;
    invoiceStats?: InvoiceStats;
    initialIds?: string[];
}

export const InvoicesViewContext =
    createContext<InvoicesViewContextValue | null>(null);

export function useInvoicesView() {
    const context = useContext(InvoicesViewContext);
    if (!context) {
        throw new Error(
            "useInvoicesView must be used within InvoicesViewProvider",
        );
    }
    return context;
}
